import React, { useEffect, useMemo } from "react";
//@ts-ignore
import FileIcon from "../../../../assets/images/file.png";
import { Plus, Search } from "lucide-react";
import CrossBig from "../../../../assets/svg/CrossBig";
import CustomInput from "../../../../components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import LoadingAnimation from "../../../../assets/svg/LoadingAnimation";
import { colors } from "../../../../utils/theme";
import regexList from "../../../../utils/regexList";
import { isNullOrEmpty } from "../../../../utils/helperFunction";
import { getPartnerAgencies } from "../../../../endpoints/hiringCompany/requirements";
import { PartnerCompaniesFields } from "./partnerCompanies.types";
import { sendHiringCompanyInvite } from "../../../../endpoints/recruiter/requirements";
import { useSnackbar } from "notistack";

const PartnerCompanies: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = useForm<PartnerCompaniesFields>({
    defaultValues: {
      inviteModal: false,
      isSubmitting: false,
      data: [],
      isLoading: true,
    },
  });
  const fields = watch();

  const onInvite = async (data) => {
    try {
      setValue("isSubmitting", true);
      const response = await sendHiringCompanyInvite(data);
      if (response?.success) {
        await fetchData();
        setValue("inviteModal", false);
        setValue("firstName", undefined);
        setValue("lastName", undefined);
        setValue("email", undefined);
        enqueueSnackbar(response?.data?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      console.error(error, "error");
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setValue("isSubmitting", false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await getPartnerAgencies();
      setValue("data", response?.data);
    } catch (error) {
    } finally {
      setValue("isLoading", false);
    }
  };

  const filteredData = useMemo(() => {
    if (isNullOrEmpty(fields?.search)) {
      return fields?.data;
    }

    return fields?.data?.filter((x) =>
      x.hiringCompany.companyName
        ?.toLowerCase()
        ?.includes(fields?.search?.trim()?.toLowerCase())
    );
  }, [fields?.data, fields?.search]);

  if (fields?.isLoading && isNullOrEmpty(filteredData)) {
    return (
      <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  const formatNumber = (num: string | number) => {
    return Number(num) > 9 ? num : num.toString().padStart(2, "0");
  };

  return (
    <div className="flex flex-1">
      {!isNullOrEmpty(fields?.data) ? (
        <div className="w-[100%] overflow-x-auto no-scrollbar">
          <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg px-3 py-3 my-5">
            <div className="relative w-1/3">
              {/* @ts-ignore */}
              <CustomInput
                name="search"
                control={control}
                className="border-none"
                errors={errors}
                placeholder="Search by company name..."
                renderRight={<Search color="#6F6F6F" width={16} />}
              />
            </div>
            <button
              className="bg-[#4D4D4D] text-white px-3 py-[10px] font-semibold rounded-md text-[10px]"
              onClick={() => setValue("inviteModal", !fields?.inviteModal)}
            >
              Invite hiring company
            </button>
          </div>
          {!isNullOrEmpty(filteredData) ? (
            <table className="min-w-full text-xs text-left">
              <thead className="border-b border-[#E0E0E0]">
                <tr className="font-semibold">
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Company name
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    First name
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Last name
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F] text-center">
                    Open jobs
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Email address
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {filteredData?.map((job, idx) => (
                  <tr
                    key={idx}
                    className="border-t  border-gray-100 cursor-pointer hover:bg-gray-50"
                  >
                    <td className="text-xs font-medium text-[#6F6F6F] ">
                      <div className="flex gap-2 items-center">
                        <div
                          className={`h-8 w-8 rounded-full justify-center items-center flex bg-nwhited ${
                            !job?.hiringCompany?.profilePic && "bg-[#E1E0E0]"
                          }`}
                        >
                          {job?.hiringCompany?.profilePic ? (
                            <img
                              src={job?.hiringCompany?.profilePic}
                              className="h-full w-full object-cover rounded-full"
                              alt="hiringCompany_profilePic"
                            />
                          ) : (
                            <span className="font-montserrat text-white font-semibold text-sm uppercase">
                              {job.hiringCompany?.companyName?.charAt(0)}
                            </span>
                          )}
                        </div>
                        {job.hiringCompany?.companyName}
                      </div>
                    </td>
                    <td className="py-6 text-xs font-medium text-[#6F6F6F]">
                      {job.firstName}
                    </td>
                    <td className="py-6 text-xs text-[#6F6F6F]">
                      {job.lastName}
                    </td>
                    <td className="py-6 text-xs text-[#6F6F6F] text-center">
                      {formatNumber(job.totalOpenJobs)}
                    </td>
                    <td className="py-6 text-xs font-medium text-[#6F6F6F]">
                      {job.hiringCompany?.email}
                    </td>
                    <td className="py-6 text-xs text-[#6F6F6F]">-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            isNullOrEmpty(filteredData) && (
              <div className="flex flex-col items-center justify-center flex-1 text-center">
                <img src={FileIcon} alt="File Icon" className="w-48 h-48" />
                <p className="text-xs text-[#6F6F6F] font-medium mt-4">
                  No search result found!
                </p>
              </div>
            )
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          <img src={FileIcon} alt="File Icon" className="w-48 h-48" />
          <p className="text-xs text-[#6F6F6F] font-medium mt-4">
            You currently don’t have any patterned company.
          </p>
          <p className="text-sm text-charcoal font-semibold mt-6 whitespace-pre">
            Click here to invite.
          </p>
          <button
            className="flex items-center justify-center border bg-green-600 mt-3 rounded-lg px-4 py-2 gap-1 text-white font-semibold text-sm"
            onClick={() => setValue("inviteModal", !fields?.inviteModal)}
          >
            <Plus width={18} height={18} /> Invite
          </button>
        </div>
      )}

      {fields?.inviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-semibold">Invite company</h2>
              <button onClick={() => setValue("inviteModal", false)}>
                <CrossBig width={24} height={24} />
              </button>
            </div>
            <div className="bg-[#F9FAFB] px-8 py-3 rounded-2xl">
              <div className="mb-4">
                {/* @ts-ignore */}
                <CustomInput
                  label="First Name"
                  name="firstName"
                  control={control}
                  errors={errors}
                  rules={{
                    required: "This field is required.",
                  }}
                />
              </div>
              <div className="mb-4">
                {/* @ts-ignore */}
                <CustomInput
                  label="Last Name"
                  name="lastName"
                  control={control}
                  errors={errors}
                  rules={{
                    required: "This field is required.",
                  }}
                />
              </div>
              <div className="mb-4">
                {/* @ts-ignore */}
                <CustomInput
                  label="Email address"
                  name="email"
                  control={control}
                  errors={errors}
                  rules={{
                    required: "This field is required.",
                    pattern: {
                      value: regexList.email,
                      message: "Please enter a valid email.",
                    },
                  }}
                />
              </div>
              <button
                onClick={handleSubmit(onInvite)}
                className="text-base font-bold bg-primary text-white py-2 px-6 rounded-md flex items-center justify-self-end mt-14 mb-6"
              >
                {fields.isSubmitting && (
                  <LoadingAnimation color={colors.White} />
                )}
                Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCompanies;
